import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Eye,
  Download,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  RefreshCw,
  Layers,
  Globe,
  Lock,
  Calendar,
  
  Tag
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { slideService, Slide } from './slideService';

const SlideList: React.FC = () => {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

const fetchSlides = async () => {
  try {
    setLoading(true);
    console.log('Fetching slides from:', '/api/admin/slides');
    
    const params: { page: number; per_page: number; search?: string } = {
      page: currentPage,
      per_page: itemsPerPage,
    };
    
    if (searchTerm && searchTerm.trim() !== '') {
      params.search = searchTerm;
    }
    
    console.log('Request params:', params);
    
    const response = await slideService.getAll(params);
    
    console.log('API Response:', response);
    
    if (response && response.success) {
      setSlides(response.data || []);
      
      if (response.meta) {
        setTotalItems(response.meta.total || 0);
      } else {
        setTotalItems(response.data?.length || 0);
      }
      
      console.log(`Loaded ${response.data?.length || 0} slides`);
    } else {
      console.error('API returned unsuccessful response:', response);
      toast.error('Failed to fetch slides: ' + (response?.message || 'Unknown error'));
      setSlides([]);
      setTotalItems(0);
    }
  } catch (error: any) {
    console.error('Fetch error details:', {
      error,
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL,
      }
    });
    
    // Show specific error message based on status
    if (error.response?.status === 404) {
      toast.error('API endpoint not found. Check if routes are registered.');
    } else if (error.response?.status === 401) {
      toast.error('Unauthorized. Please login again.');
    } else if (error.response?.status === 500) {
      toast.error('Server error. Check Laravel logs.');
    } else {
      toast.error(`Failed to fetch slides: ${error.message}`);
    }
    
    setSlides([]);
    setTotalItems(0);
  } finally {
    setLoading(false);
    setRefreshing(false);
  }
};
  useEffect(() => {
    fetchSlides();
  }, [currentPage, searchTerm]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSlides();
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this slide?')) {
      try {
        const response = await slideService.delete(id);
        
        if (response.success) {
          toast.success('Slide deleted successfully');
          fetchSlides();
        } else {
          toast.error(response.message || 'Failed to delete slide');
        }
      } catch (error: any) {
        console.error('Delete error:', error);
        toast.error(error.response?.data?.message || 'Failed to delete slide');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error('Please select items to delete');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ${selectedRows.length} item(s)?`)) {
      try {
        const response = await slideService.bulkDelete(selectedRows);
        
        if (response.success) {
          toast.success(`${response.deleted_count || selectedRows.length} item(s) deleted successfully`);
          setSelectedRows([]);
          fetchSlides();
        } else {
          toast.error(response.message || 'Failed to delete items');
        }
      } catch (error: any) {
        console.error('Bulk delete error:', error);
        toast.error('Failed to delete items');
      }
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    try {
      const response = await slideService.updateStatus(id, !currentStatus);
      
      if (response.success) {
        toast.success(`Slide ${!currentStatus ? 'activated' : 'deactivated'}`);
        fetchSlides();
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error: any) {
      console.error('Status update error:', error);
      toast.error('Failed to update status');
    }
  };

  const handleTogglePublish = async (id: number, currentStatus: boolean) => {
    try {
      const response = await slideService.updatePublishStatus(id, !currentStatus);
      
      if (response.success) {
        toast.success(`Slide ${!currentStatus ? 'published' : 'unpublished'}`);
        fetchSlides();
      } else {
        toast.error(response.message || 'Failed to update publish status');
      }
    } catch (error: any) {
      console.error('Publish update error:', error);
      toast.error('Failed to update publish status');
    }
  };

  const handleBulkActivate = async () => {
    if (selectedRows.length === 0) {
      toast.error('Please select items to activate');
      return;
    }
    
    try {
      const promises = selectedRows.map(id => 
        slideService.updateStatus(id, true)
      );
      
      await Promise.all(promises);
      toast.success(`${selectedRows.length} item(s) activated`);
      setSelectedRows([]);
      fetchSlides();
    } catch (error) {
      toast.error('Failed to activate items');
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedRows.length === 0) {
      toast.error('Please select items to deactivate');
      return;
    }
    
    try {
      const promises = selectedRows.map(id => 
        slideService.updateStatus(id, false)
      );
      
      await Promise.all(promises);
      toast.success(`${selectedRows.length} item(s) deactivated`);
      setSelectedRows([]);
      fetchSlides();
    } catch (error) {
      toast.error('Failed to deactivate items');
    }
  };

  const handleBulkPublish = async () => {
    if (selectedRows.length === 0) {
      toast.error('Please select items to publish');
      return;
    }
    
    try {
      const promises = selectedRows.map(id => 
        slideService.updatePublishStatus(id, true)
      );
      
      await Promise.all(promises);
      toast.success(`${selectedRows.length} item(s) published`);
      setSelectedRows([]);
      fetchSlides();
    } catch (error) {
      toast.error('Failed to publish items');
    }
  };

  const handleBulkUnpublish = async () => {
    if (selectedRows.length === 0) {
      toast.error('Please select items to unpublish');
      return;
    }
    
    try {
      const promises = selectedRows.map(id => 
        slideService.updatePublishStatus(id, false)
      );
      
      await Promise.all(promises);
      toast.success(`${selectedRows.length} item(s) unpublished`);
      setSelectedRows([]);
      fetchSlides();
    } catch (error) {
      toast.error('Failed to unpublish items');
    }
  };

  const filteredItems = slides.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.category?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (item.full_description?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Slides</h1>
          <p className="text-gray-600">Manage all slides displayed on the homepage slider</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2 disabled:opacity-50"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
          <Link
            to="/admin/home/slides/create"
            className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all flex items-center space-x-2 shadow-sm"
          >
            <Plus size={18} />
            <span>Add Slide</span>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-800">{totalItems}</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
              <Layers className="text-blue-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">{slides.filter(f => f.is_active).length}</p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Published</p>
              <p className="text-2xl font-bold text-blue-600">{slides.filter(f => f.is_published).length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
              <Globe className="text-blue-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Inactive</p>
              <p className="text-2xl font-bold text-red-600">{slides.filter(f => !f.is_active).length}</p>
            </div>
            <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
              <XCircle className="text-red-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Last Updated</p>
              <p className="text-lg font-semibold text-gray-800">
                {slides.length > 0 
                  ? new Date(slides[0].updated_at || slides[0].created_at).toLocaleDateString() 
                  : 'N/A'
                }
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
              <Calendar className="text-purple-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search slides by title, category, description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2">
              <Filter size={18} />
              <span>Filter</span>
            </button>
            <button className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2">
              <Download size={18} />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedRows.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 font-medium">{selectedRows.length}</span>
            </div>
            <p className="text-blue-700">{selectedRows.length} item(s) selected</p>
          </div>
          <div className="flex items-center space-x-3 flex-wrap gap-2">
            <button 
              onClick={handleBulkActivate}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Activate
            </button>
            <button 
              onClick={handleBulkDeactivate}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
            >
              Deactivate
            </button>
            <button 
              onClick={handleBulkPublish}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Publish
            </button>
            <button 
              onClick={handleBulkUnpublish}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Unpublish
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              <Trash2 size={16} />
              <span>Delete</span>
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading slides...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={selectedRows.length === slides.length && slides.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRows(slides.map(item => item.id));
                        } else {
                          setSelectedRows([]);
                        }
                      }}
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Image</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Title</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Order</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Published</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <Layers className="text-gray-400" size={24} />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">No slides found</h3>
                        <p className="text-gray-500 mb-4">
                          {searchTerm ? `No results for "${searchTerm}"` : 'Get started by creating a new slide'}
                        </p>
                        {!searchTerm && (
                          <Link
                            to="/admin/home/slides/create"
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Add Slide
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(item.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRows([...selectedRows, item.id]);
                            } else {
                              setSelectedRows(selectedRows.filter(id => id !== item.id));
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>

                      <td className="px-6 py-4">
                        <div className="w-20 h-16 rounded-lg overflow-hidden bg-gray-100">
                          <img
                            src={item.image_url}
                            alt={item.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error('Image failed to load:', item.image_url);
                              // Fallback to placeholder with first letter
                              const firstLetter = item.title?.charAt(0) || 'S';
                              (e.target as HTMLImageElement).src = `https://placehold.co/150/2563eb/white?text=${firstLetter}`;
                            }}
                          />
                        </div>
                      </td>
                      




                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-800">{item.title}</p>
                          <p className="text-sm text-gray-600 truncate max-w-xs">
                            {item.full_description?.substring(0, 60)}...
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-gray-800 font-medium">
                          {item.order}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleStatus(item.id, item.is_active)}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            item.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          {item.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleTogglePublish(item.id, item.is_published)}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            item.is_published
                              ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {item.is_published ? (
                            <>
                              <Globe size={14} className="mr-1" />
                              Published
                            </>
                          ) : (
                            <>
                              <Lock size={14} className="mr-1" />
                              Draft
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        {item.category ? (
                          <span className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                            <Tag size={12} className="mr-1" />
                            {item.category}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/admin/home/slides/edit/${item.id}`}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit size={18} />
                          </Link>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                          <button
                            onClick={() => window.open(`/slider/${item.id}`, '_blank')}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                            title="View"
                          >
                            <Eye size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`w-10 h-10 rounded-lg transition-colors ${
                    currentPage === i + 1
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlideList;